import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress, Typography, Grid, FormControlLabel, Checkbox, Alert, Box,
} from '@mui/material';
import dayjs from 'dayjs';
import { InvTextField, InvSelect } from '../InvField';
import { acceptVendorQuote } from '../../api/rfq';
import { createPoFromAcceptedQuote } from '../../api/procurement';
import { toAcceptQuoteBody, toPoFromQuoteBody, vendorDisplayLabel, resolveRfqVendorLabel } from '../../utils/rfqEntity';
import {
  rankQuotes, awardBlockers, approvalRoute, getQuoteId,
} from '../../utils/rfqComparison';
import { NON_L1_REASONS, MIN_AWARD_JUSTIFICATION_CHARS } from '../../constants/rfqManagement';
import { generateTxnNumber, formatCurrency } from '../../utils';
import { IPD_COLORS, cardSx } from '../../theme';

export default function RfqAwardDialog({
  open,
  quote,
  comparison,
  rfq,
  rfqItems = [],
  vendorDirectory = [],
  onClose,
  onSuccess,
}) {
  const quoteId = getQuoteId(quote);
  const [remarks, setRemarks] = useState('');
  const [justification, setJustification] = useState('');
  const [nonL1Reason, setNonL1Reason] = useState('');
  const [rejectOthers, setRejectOthers] = useState(true);
  const [createPo, setCreatePo] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [poNumber, setPoNumber] = useState(() => generateTxnNumber('PO'));
  const [poDate, setPoDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [expectedDelivery, setExpectedDelivery] = useState(dayjs().add(30, 'day').format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const ranked = useMemo(() => rankQuotes(comparison), [comparison]);
  const target = useMemo(
    () => ranked.find((q) => q.quoteId === quoteId),
    [ranked, quoteId],
  );
  const vendor = useMemo(
    () => vendorDirectory.find((v) => (v.vendor_id || v.id) === quote?.vendor_id),
    [vendorDirectory, quote],
  );

  const awardValue = target?.net ?? quote?.total_net_amount;

  const blockers = useMemo(() => awardBlockers({
    quote,
    ranked,
    rfqItems,
    vendor,
    minimumQuotes: rfq?.minimum_required_quotes ?? rfq?.required_quotes_count,
    justification,
    nonL1Reason,
  }), [quote, ranked, rfqItems, vendor, rfq, justification, nonL1Reason]);

  const needsJustification = blockers.some((b) => b.key === 'below-minimum')
    || (rfq?.minimum_required_quotes && ranked.length < Number(rfq.minimum_required_quotes));
  const isNonL1 = Boolean(target?.rank && target.rank > 1);
  const blocked = blockers.length > 0 || !confirmed;

  async function handleSubmit() {
    if (!quoteId || blocked) return;
    setLoading(true);
    setError(null);
    try {
      // The service takes a single free-text remark, so the audit trail for why
      // this award was allowed has to travel inside it.
      const trail = [
        remarks.trim(),
        justification.trim() && `Justification: ${justification.trim()}`,
        nonL1Reason && `Not L1 — ${NON_L1_REASONS.find((r) => r.value === nonL1Reason)?.label || nonL1Reason}`,
      ].filter(Boolean).join(' | ');

      const accepted = await acceptVendorQuote(quoteId, toAcceptQuoteBody({
        remarks: trail,
        reject_others: rejectOthers,
      }));
      const acceptedQuoteId = accepted?.accepted_quote_id || accepted?.id;

      let poId = null;
      if (createPo && acceptedQuoteId) {
        const po = await createPoFromAcceptedQuote(toPoFromQuoteBody(acceptedQuoteId, {
          po_number: poNumber,
          po_date: poDate,
          expected_delivery_date: expectedDelivery,
          remarks: trail,
        }));
        poId = po?.id || po?.purchase_order_id;
      }

      onSuccess?.({ acceptedQuoteId, poId });
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to accept quote');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...cardSx, p: 0.5 } }}
    >
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>
        Accept quote and award RFQ
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
          Accept quote <strong>{quote?.quote_number || quoteId}</strong> from{' '}
          <strong>
            {vendor
              ? vendorDisplayLabel(vendor)
              : resolveRfqVendorLabel(quote?.vendor_id, { quote })}
          </strong>
          {target ? <> — <strong>{target.rankLabel}</strong> at {formatCurrency(target.net)}</> : null}.
          This marks the RFQ as <strong>AWARDED</strong>.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {blockers.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              {blockers.length} {blockers.length === 1 ? 'thing' : 'things'} to resolve first
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {blockers.map((b) => (
                <Box component="li" key={b.key} sx={{ fontSize: 13 }}>{b.message}</Box>
              ))}
            </Box>
          </Alert>
        )}

        {awardValue != null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Award value {formatCurrency(awardValue)} — approval route:{' '}
            <strong>{approvalRoute(awardValue).join(' → ')}</strong>
          </Alert>
        )}

        <Grid container spacing={2}>
          {needsJustification && (
            <Grid item xs={12}>
              <InvTextField
                label="Justification for awarding below the minimum quote count"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                multiline
                rows={2}
                helperText={`At least ${MIN_AWARD_JUSTIFICATION_CHARS} characters — ${justification.trim().length} so far.`}
              />
            </Grid>
          )}

          {isNonL1 && (
            <Grid item xs={12}>
              <InvSelect
                label="Reason for not awarding L1"
                value={nonL1Reason}
                onChange={(e) => setNonL1Reason(e.target.value)}
                options={NON_L1_REASONS}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <InvTextField
              label="Acceptance remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={rejectOthers}
                  onChange={(e) => setRejectOthers(e.target.checked)}
                  sx={{ color: IPD_COLORS.primary }}
                />
              )}
              label="Reject all other quotes"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createPo}
                  onChange={(e) => setCreatePo(e.target.checked)}
                  sx={{ color: IPD_COLORS.primary }}
                />
              )}
              label="Create purchase order from accepted quote"
            />
          </Grid>
          {createPo && (
            <>
              <Grid item xs={12} sm={6}>
                <InvTextField label="PO number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InvTextField label="PO date" type="date" value={poDate}
                  onChange={(e) => setPoDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <InvTextField label="Expected delivery" type="date" value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  sx={{ color: IPD_COLORS.primary }}
                />
              )}
              label={(
                <Typography sx={{ fontSize: 13 }}>
                  I confirm this award is correct.
                </Typography>
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {blockers.length > 0
            ? `${blockers.length} to resolve first`
            : (confirmed ? 'Ready to award' : 'Tick the confirmation to continue')}
        </Typography>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading || blocked} variant="contained"
          sx={{ backgroundColor: IPD_COLORS.primary }}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}>
          {awardValue != null ? `Award ${formatCurrency(awardValue)}` : 'Accept and award'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
